export type BadgeVariant =
  | 'default'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'purple'
  | 'blue'
  | 'yellow';

export interface StatusConfigEntry {
  label: string;
  variant: BadgeVariant;
}

const VARIANT_CLASSES: Record<BadgeVariant, string> = {
  default: 'bg-neutral-500/10 text-neutral-400 border-neutral-500/30',
  success: 'bg-green-500/10 text-green-400 border-green-500/30',
  warning: 'bg-orange-500/10 text-orange-400 border-orange-500/30',
  error: 'bg-red-500/10 text-red-400 border-red-500/30',
  info: 'bg-teal-500/10 text-teal-400 border-teal-500/30',
  purple: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
  blue: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
  yellow: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
};

const DAPP_STATUS_CONFIG: Record<string, StatusConfigEntry> = {
  Published: { label: 'Published', variant: 'success' },
  UnderReview: { label: 'Under Review', variant: 'warning' },
  Rejected: { label: 'Rejected', variant: 'error' },
  Draft: { label: 'Draft', variant: 'default' },
  Archived: { label: 'Archived', variant: 'default' },
};

const RELEASE_STATUS_CONFIG: Record<string, StatusConfigEntry> = {
  Published: { label: 'Published', variant: 'success' },
  Processing: { label: 'Processing', variant: 'info' },
  Processed: { label: 'Processed', variant: 'success' },
  ProcessingFailed: { label: 'Processing Failed', variant: 'error' },
  Rejected: { label: 'Rejected', variant: 'error' },
  Archived: { label: 'Archived', variant: 'default' },
  Failed: { label: 'Failed', variant: 'error' },
  Draft: { label: 'Draft', variant: 'default' },
};

const SUBMISSION_STATUS_CONFIG: Record<string, StatusConfigEntry> = {
  NotSubmitted: { label: 'Not Submitted', variant: 'default' },
  Submitted: { label: 'Submitted', variant: 'blue' },
  UnderReview: { label: 'Under Review', variant: 'yellow' },
  AlphaTest: { label: 'Alpha Testing', variant: 'purple' },
  Approved: { label: 'Approved', variant: 'success' },
  Rejected: { label: 'Rejected', variant: 'error' },
  RequiresChanges: { label: 'Requires Changes', variant: 'warning' },
  ProcessingFailed: { label: 'Processing Failed', variant: 'error' },
};

const ROLE_STATUS_CONFIG: Record<string, StatusConfigEntry> = {
  superadmin: { label: 'Super Admin', variant: 'error' },
  admin: { label: 'Admin', variant: 'warning' },
  moderator: { label: 'Moderator', variant: 'info' },
  base: { label: 'Base User', variant: 'default' },
  user: { label: 'User', variant: 'default' },
};

const SYNC_STATUS_CONFIG: Record<string, StatusConfigEntry> = {
  success: { label: 'Success', variant: 'success' },
  error: { label: 'Error', variant: 'error' },
  warning: { label: 'Warning', variant: 'warning' },
  unknown: { label: 'Unknown', variant: 'default' },
};

const EMAIL_STATUS_CONFIG: Record<string, StatusConfigEntry> = {
  Sent: { label: 'Sent', variant: 'success' },
  Sending: { label: 'Sending', variant: 'info' },
  Failed: { label: 'Failed', variant: 'error' },
  Pending: { label: 'Pending', variant: 'warning' },
};

const KYB_STATUS_CONFIG: Record<string, StatusConfigEntry> = {
  NotStarted: { label: 'Not Started', variant: 'default' },
  InProgress: { label: 'In Progress', variant: 'info' },
  UnderReview: { label: 'Under Review', variant: 'warning' },
  Approved: { label: 'Approved', variant: 'success' },
  Rejected: { label: 'Rejected', variant: 'error' },
  RequiresAdditionalInfo: { label: 'Requires Info', variant: 'warning' },
};

const AUDIT_STATUS_CONFIG: Record<string, StatusConfigEntry> = {
  success: { label: 'Success', variant: 'success' },
  failure: { label: 'Failure', variant: 'error' },
  pending: { label: 'Pending', variant: 'warning' },
};

export type StatusType =
  | 'audit'
  | 'dapp'
  | 'email'
  | 'kyb'
  | 'release'
  | 'role'
  | 'submission'
  | 'sync';

const STATUS_CONFIG_MAP: Record<
  StatusType,
  Record<string, StatusConfigEntry>
> = {
  dapp: DAPP_STATUS_CONFIG,
  release: RELEASE_STATUS_CONFIG,
  email: EMAIL_STATUS_CONFIG,
  kyb: KYB_STATUS_CONFIG,
  audit: AUDIT_STATUS_CONFIG,
  submission: SUBMISSION_STATUS_CONFIG,
  role: ROLE_STATUS_CONFIG,
  sync: SYNC_STATUS_CONFIG,
};

export function getStatusConfig(
  type: StatusType,
  status: string,
): StatusConfigEntry {
  const config = STATUS_CONFIG_MAP[type];
  return (
    config[status] ?? { label: status, variant: 'default' as BadgeVariant }
  );
}

export function getVariantClasses(variant: BadgeVariant): string {
  return VARIANT_CLASSES[variant];
}

export function getDappStatusVariant(status: string): BadgeVariant {
  return getStatusConfig('dapp', status).variant;
}

export function getStatusVariant(
  type: StatusType,
  status: string,
): BadgeVariant {
  return getStatusConfig(type, status).variant;
}
